import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';

interface CardProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
  hoverable?: boolean;
  padding?: 'none' | 'sm' | 'md' | 'lg';
}

const paddingStyles = {
  none: '',
  sm: 'p-3',
  md: 'p-4 md:p-5',
  lg: 'p-5 md:p-6',
};

export const Card = forwardRef<HTMLDivElement, CardProps>(
  ({ children, hoverable = false, padding = 'md', className = '', ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={[
          'bg-[var(--color-surface)] border border-[var(--color-border)] rounded-[var(--r-xl)]',
          hoverable &&
            'hover:shadow-[var(--shadow-md)] hover:border-[var(--color-border-2)] cursor-pointer transition-shadow',
          paddingStyles[padding],
          className,
        ]
          .filter(Boolean)
          .join(' ')}
        {...props}
      >
        {children}
      </div>
    );
  },
);

Card.displayName = 'Card';

interface CardHeaderProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
}

export function CardHeader({ children, className = '', ...props }: CardHeaderProps) {
  return (
    <div
      className={`flex items-center justify-between pb-3 mb-3 border-b border-[var(--color-border)] ${className}`}
      {...props}
    >
      {children}
    </div>
  );
}

interface CardTitleProps extends HTMLAttributes<HTMLHeadingElement> {
  children: ReactNode;
}

export function CardTitle({ children, className = '', ...props }: CardTitleProps) {
  return (
    <h3
      className={`text-lead font-medium text-[var(--color-ink)] ${className}`}
      {...props}
    >
      {children}
    </h3>
  );
}
